import { DisaggregationResponse } from '@api/disaggregation';
import { ColumnDef } from '@tanstack/react-table';
import { MinimalUtrv, UniversalTrackerValueView } from '@features/universal-tracker-value';
import { Table } from '@g17eco/molecules/table';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { useHistory } from 'react-router-dom';
import { getPeriodName } from '@utils/dataPeriods';
import { DATE, formatDateUTC } from '@utils/date';
import { useAppSelector } from '../../../../reducers';
import { getInitiativeTree } from '@selectors/initiativeTree';
import { getBranchInitiativeNameText } from '@routes/initiative-structure/utils';
import './styles.scss';
import { naturalSort } from '@utils/index';
import { getSupportedValueText, sortBySubsidiary } from './utils';
import { ExtendedHistoryData } from './types';
import { useMemo, useState } from 'react';
import { getStatusIcon } from '@utils/universalTrackerValue';
import { isStaffSuperUser, isOrgManager, isStaff } from '@selectors/user';
import { RefreshAggregatedData } from './RefreshAggregatedData';
import { UnitConfig, SurveyModelMinimalUtrv } from '@g17eco/types/survey';
import {
  AggregationConfigOverride,
  AggregationOverrideData,
  InitiativeUniversalTracker,
} from '@g17eco/types/initiativeUniversalTracker';
import UniversalTrackerClassInterface from '@models/UniversalTrackerClassInterface';
import { ValueAggregation } from '@g17eco/core';
import { AggregationDisplay } from './AggregationDisplay';
import { AggregationOverrideForm } from '@features/question-configuration/aggregation-override/AggregationOverrideForm';
import { useOverrideAggregationMutation } from '@api/initiative-universal-trackers';
import { getInitialAggregationOverrideData } from '@features/question-configuration/aggregation-override/utils';
import { getRootOrg } from '@selectors/initiative';
import { QueryError } from '@g17eco/molecules/query/QueryError';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { SURVEY } from '@constants/terminology';

interface Props {
  utr?: UniversalTrackerClassInterface;
  utrv?: SurveyModelMinimalUtrv & { aggregationConfig?: AggregationConfigOverride };
  data?: DisaggregationResponse;
  aggregatedDate?: string;
  onRefresh?: () => void;
  unitConfig?: UnitConfig;
  initiativeUtrs: InitiativeUniversalTracker[];
}

export const AggregatedBreakdownDetail = (props: Props) => {
  const { utr, utrv, data, aggregatedDate, onRefresh, unitConfig, initiativeUtrs } = props;
  const history = useHistory();
  const isSuperUser = useAppSelector(isStaffSuperUser);
  const canManageOrg = useAppSelector(isOrgManager);
  const tree = useAppSelector(getInitiativeTree);
  const organization = useAppSelector(getRootOrg);
  const isUserStaff = useAppSelector(isStaff);

  const [showAggregationModal, setShowAggregationModal] = useState(false);
  const [updateAggregationOverride, { isLoading: isUpdating, error, isError }] = useOverrideAggregationMutation();

  const initiativeUtrMap = new Map<string, InitiativeUniversalTracker>(
    initiativeUtrs.map((utr) => [utr.universalTrackerId, utr]),
  );

  const handleUpdateAggregationOverride = ({ aggregationConfig, table }: AggregationOverrideData) => {
    if (!utr || !organization || !canManageOrg) return;

    updateAggregationOverride({
      initiativeId: organization._id,
      utrId: utr.getId(),
      aggregationConfig,
      table,
    })
      .unwrap()
      .then(() => {
        setShowAggregationModal(false);
      });
  };

  const disaggregatedData: ExtendedHistoryData[] = useMemo(() => {
    if (!utr || !utrv || !data) {
      return [];
    }
    return data.disaggregatedData
      .map((rowData, index) => {
        let displayAnswer = '';

        if (index !== 0 && data.valueAggregation === ValueAggregation.LatestAggregator) {
          displayAnswer = 'Ignore';
        } else {
          displayAnswer = getSupportedValueText(rowData, utr);
        }

        return {
          ...rowData,
          displaySubsidiary: getBranchInitiativeNameText({
            initiativeTree: tree,
            initiativeId: rowData.initiativeId,
            showInitiativeId: true,
          }),
          displayDate: formatDateUTC(rowData.effectiveDate, DATE.MONTH_YEAR_SHORT),
          displayPeriod: getPeriodName(rowData.period),
          displayAnswer,
        };
      })
      .sort(sortBySubsidiary);
  }, [data, tree, utr, utrv]);

  if (!utr || !utrv || !data) {
    return null;
  }

  const columns: ColumnDef<ExtendedHistoryData>[] = [
    {
      header: 'Subsidiary',
      accessorFn: (row) => row.displaySubsidiary,
      sortingFn: (a, b) => sortBySubsidiary(a.original, b.original),
    },
    {
      header: 'Date',
      accessorFn: (row) => row.displayDate,
    },
    {
      header: 'Period',
      accessorFn: (row) => row.displayPeriod,
    },
    {
      header: 'Answer',
      accessorFn: (row) => row.displayAnswer,
      cell: ({ row }) => {
        if (row.index !== 0 && data.valueAggregation === ValueAggregation.LatestAggregator) {
          return <div className='text-danger'>Ignore</div>;
        }
        const minimalUtrv: MinimalUtrv = {
          _id: row.original.utrvId,
          status: utrv.status,
          value: row.original.value,
          valueData: row.original.valueData,
          initiativeId: utrv.initiativeId,
        };
        return (
          <div className='aggregated-breakdown__table-answer-col text-truncate'>
            <UniversalTrackerValueView
              utr={utr}
              utrv={minimalUtrv}
              valueType={utr.getValueType()}
              unitConfig={unitConfig}
            />
          </div>
        );
      },
      sortingFn: (a, b) => naturalSort(a.original.displayAnswer, b.original.displayAnswer),
    },
    {
      id: 'status',
      cell: ({ row }) => {
        const { utrvId, action: updatedAction, latestHistory } = row.original;
        const action = latestHistory?.action ?? updatedAction;
        return (
          <i
            data-testid={`${utrvId}-status`}
            aria-label={action}
            className={`fa-light ${getStatusIcon({ status: action })}`}
          />
        );
      },
    },
    {
      id: 'view',
      header: '',
      cell: ({ row }) => {
        if (!row.original.surveyId) {
          return null;
        }
        const surveyPath = generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, {
          initiativeId: row.original.initiativeId,
          surveyId: row.original.surveyId,
          page: 'question',
        });
        return (
          <Button color='link' onClick={() => history.push({ pathname: `${surveyPath}/${row.original.utrvId}` })}>
            View
          </Button>
        );
      },
    },
  ];

  const date = formatDateUTC(aggregatedDate ?? utrv.lastUpdated, DATE.AT_TIME);

  return (
    <>
      {isError && error ? <QueryError error={error} type={'danger'} /> : null}
      {isSuperUser && utr && utrv && (
        <RefreshAggregatedData utr={utr} utrv={utrv} unitConfig={unitConfig} onRefresh={onRefresh} />
      )}
      <p>
        <strong>Data Snapshot</strong>: This report shows a snapshot taken on <strong>{date}</strong>. If any metrics
        have changed since then, please refresh the report to see the latest data.
      </p>

      {utr ? (
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <div className='flex-grow-1'>
            <AggregationDisplay valueAggregation={data.valueAggregation} aggregationConfig={data.aggregationConfig} />
          </div>
          {isUserStaff ? (
            <SimpleTooltip
              text={!canManageOrg ? 'You need admin permissions to modify aggregation settings' : undefined}
            >
              <Button
                color='secondary'
                size='sm'
                disabled={!canManageOrg}
                onClick={() => setShowAggregationModal(true)}
              >
                Aggregation settings <FeatureStability stability={'internal'} />
              </Button>
            </SimpleTooltip>
          ) : null}
        </div>
      ) : null}

      <Table responsive={true} columns={columns} data={disaggregatedData} className='aggregated-breakdown__table' />

      {utr ? (
        <Modal isOpen={showAggregationModal} size='lg'>
          <ModalHeader toggle={() => setShowAggregationModal(false)}>{utr.getName()}</ModalHeader>
          <ModalBody className='pb-0'>
            <div className='w-100 fs-6'>
              Aggregation overrides are set globally. All combined {SURVEY.PLURAL} created in the future will respect
              these rules.
            </div>
          </ModalBody>
          <AggregationOverrideForm
            isLoading={isUpdating}
            initialData={getInitialAggregationOverrideData({
              initiativeUtrMap,
              utr: utr.getRaw(),
            })}
            selectedQuestion={utr.getRaw()}
            handleUpdateAggregationOverride={handleUpdateAggregationOverride}
            handleCloseModal={() => setShowAggregationModal(false)}
            initiativeUtrMap={initiativeUtrMap}
            rootInitiativeId={organization?._id}
          />
        </Modal>
      ) : null}
    </>
  );
};
